import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useWsNavigate } from "../../hooks/useWsNavigate";
import {
  Plus,
  Play,
  Sparkles,
  X,
  UserCircle,
  Minus,
  Trash2,
  Mic,
  Pencil,
  CheckCircle,
  ArrowRight,
  Phone,
  Globe,
  MessageSquare,
  Columns2,
  Archive,
  Users,
} from "lucide-react";
import {
  scenariosAPI,
  personaAPI,
  metadataAPI,
  metricsAPI,
} from "../../services/api";
import ConversationAttributesTab from "./ConversationAttributesTab";
import ScheduledRuns from "../ScheduledRuns";
import { ScenarioSelectionToolbar } from "./ScenarioSelectionToolbar";

import { useAgent } from "../../contexts/AgentContext";
import AgentPicker from "../../components/AgentPicker";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { useAuth } from "../../contexts/AuthContext";
import { Menu } from "@headlessui/react";
import CallModal from "../../components/voice/CallModal";
import Button from "../../components/ui/Button";
import DropdownMenu from "../../components/ui/DropdownMenu";
import Pagination from "../../components/ui/Pagination";
import { Badge } from "../../components/ui/badge";
import { SearchInput } from "../../components/ui/search-input";
import useSetPageTitle from "../../hooks/useSetPageTitle";
import usePagination from "../../hooks/usePagination";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import usePlan from "../../hooks/usePlan";
import { useCan } from "../../hooks/useCan";
import { useFeature } from "../../hooks/useFeature";

export default function Scenario() {
  useSetPageTitle("Live Simulations");
  const canWriteScenario = useCan("scenario", "write");
  const canAiGenerate = useFeature("scenarios.ai_scenario_generation");
  const canStressTest = useFeature("live_simulations.stress_testing");
  const canWritePersona = useCan("persona", "write");
  const { selectedAgent, agents, setSelectedAgent, refreshAgents } = useAgent();

  // Refresh agents on mount so channel-gating sees fresh data after upstream
  // edits (agent save, vendor sync) — otherwise a cached pre-sync agent can
  // bypass the "Choose Channel" modal and run the wrong channel directly.
  useEffect(() => { refreshAgents(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Always use the freshly-fetched agent data (not stale localStorage version)
  const liveAgent =
    agents.find((a) => a.id === selectedAgent?.id) || selectedAgent;
  const navigate = useWsNavigate();
  const location = useLocation();
  const { user, currentWorkspace } = useAuth();
  const { planType, planBadge } = usePlan();

  const TAB_PATHS = {
    scenario: "/live-simulations/scenario",
    persona: "/live-simulations/persona",
    attributes: "/live-simulations/attributes",
    schedules: "/live-simulations/schedules",
  };
  const tabFromPath = (pathname) => {
    // Authenticated URLs are prefixed with the workspace UUID
    // (/<uuid>/live-simulations/persona), so match anywhere in the path —
    // startsWith fails against the prefix and would pin every tab to "scenario".
    const entry = Object.entries(TAB_PATHS).find(([, path]) =>
      pathname.includes(path),
    );
    return entry ? entry[0] : "scenario";
  };
  const activeTab = tabFromPath(location.pathname);
  const goToTab = (tab) => navigate(TAB_PATHS[tab]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [personas, setPersonas] = useState([]);
  const [metadataList, setMetadataList] = useState([]);

  // Persona tab states
  const [personaSubTab, setPersonaSubTab] = useState("all");
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [personaLoading, setPersonaLoading] = useState(false);
  const [personaError, setPersonaError] = useState(null);
  const [personaEnabledStates, setPersonaEnabledStates] = useState({});
  const [generationForm, setGenerationForm] = useState({
    agentId: "",
    numberOfScenarios: 1,
    metadata: [{ key: "", value: "" }],
    personaId: "",
    additionalInstructions: "",
    openingInstructions: "",
  });
  const [search, setSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(null);
  const { currentPage, totalPages, setCurrentPage, setTotalPages } =
    usePagination();
  const debouncedSearch = useDebouncedValue(search, 300);
  // Ignore responses a newer request has already superseded — search fires a
  // request per (debounced) keystroke, so they can land out of order.
  const requestId = React.useRef(0);

  // Changing the query invalidates the page number — page 3 of the old result
  // set usually doesn't exist in the new one.
  const changeSearch = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [successBatch, setSuccessBatch] = useState(null);
  const [showCallMethodModal, setShowCallMethodModal] = useState(false);
  const [scheduleScenarioIds, setScheduleScenarioIds] = useState([]);
  const SCENARIO_COLUMNS = [
    { key: "name", label: "Name" },
    { key: "created_at", label: "Created" },
    { key: "id", label: "ID" },
    { key: "type", label: "Type" },
    { key: "language", label: "Language" },
    { key: "persona", label: "Persona" },
    { key: "metrics", label: "Metrics" },
    { key: "tags", label: "Tags" },
  ];
  const HIDDEN_BY_DEFAULT_COLUMNS = new Set(["tags", "language"]);
  const [visibleColumns, setVisibleColumns] = useState(
    () =>
      new Set(
        SCENARIO_COLUMNS.filter(
          (c) => !HIDDEN_BY_DEFAULT_COLUMNS.has(c.key),
        ).map((c) => c.key),
      ),
  );
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const columnsMenuRef = React.useRef(null);

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        columnsMenuRef.current &&
        !columnsMenuRef.current.contains(e.target)
      ) {
        setShowColumnsMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (currentWorkspace) {
      fetchPersonas();
      fetchMetadata();
    }
    // eslint-disable-next-line
  }, [currentWorkspace]);

  useEffect(() => {
    if (currentWorkspace) fetchScenarios();
    // eslint-disable-next-line
  }, [currentPage, currentWorkspace, selectedAgent?.id, debouncedSearch]);

  useEffect(() => {
    if (!currentWorkspace) return;
    const fetchMetrics = async () => {
      try {
        const metricsRes = await metricsAPI.getAllMetrics(currentWorkspace.id);
        setMetrics(metricsRes.data || []);
      } catch (err) {
        // handle error if needed
      }
    };
    fetchMetrics();
  }, [currentWorkspace]);

  const fetchPersonas = async () => {
    try {
      const response = await personaAPI.getAllPersonas(currentWorkspace.id);
      setPersonas(response.data || []);
    } catch (err) {
      console.error("Error fetching personas:", err);
    }
  };

  const fetchMetadata = async () => {
    try {
      const response = await metadataAPI.getMetadata(currentWorkspace.id);
      setMetadataList(response.data.data || []);
    } catch (err) {
      console.error("Error fetching metadata:", err);
    }
  };

  const handleGenerateSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const formWithUser = { ...generationForm, user_id: user.id };
      await scenariosAPI.generateScenarios(formWithUser, currentWorkspace.id);
      setShowGenerateModal(false);
      fetchScenarios();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to generate scenarios",
      );
    } finally {
      setLoading(false);
    }
  };

  const addMetadataField = () => {
    setGenerationForm((prev) => ({
      ...prev,
      metadata: [...prev.metadata, { key: "", value: "" }],
    }));
  };

  const deleteMetadataField = (index) => {
    setGenerationForm((prev) => ({
      ...prev,
      metadata: prev.metadata.filter((_, i) => i !== index),
    }));
  };

  const updateMetadataField = (index, field, value) => {
    setGenerationForm((prev) => ({
      ...prev,
      metadata: prev.metadata.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const handleExistingMetadataSelect = (metadata) => {
    setGenerationForm((prev) => ({
      ...prev,
      metadata: [
        ...prev.metadata,
        { key: metadata.name, value: metadata.value },
      ],
    }));
  };

  const fetchScenarios = async () => {
    const id = ++requestId.current;
    try {
      setLoading(true);
      const response = await scenariosAPI.getScenariosWithPagination(
        currentPage,
        currentWorkspace.id,
        selectedAgent?.id || null,
        { search: debouncedSearch },
      );
      if (id !== requestId.current) return;
      setScenarios(response.data.data || []);
      setTotalPages(response.data.pagination.total_pages);
      setError(null);
    } catch (err) {
      if (id !== requestId.current) return;
      console.error("Error fetching scenarios:", err);
      setError(
        err.response?.data?.error || err.message || "Failed to fetch scenarios",
      );
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  const handleArchive = async (id) => {
    if (
      !window.confirm(
        "Archive this scenario? It will be hidden from the active list but existing runs are preserved.",
      )
    ) {
      return;
    }
    try {
      await scenariosAPI.archiveScenario(id, currentWorkspace.id);
      fetchScenarios();
    } catch (err) {
      setError("Failed to archive scenario");
      console.error("Error archiving scenario:", err);
    }
  };

  const handleCheckboxChange = (scenarioId) => {
    setSelectedScenarios((prev) =>
      prev.includes(scenarioId)
        ? prev.filter((id) => id !== scenarioId)
        : [...prev, scenarioId],
    );
  };

  // Search is applied server-side (it spans every page), so the rows we were
  // handed are already the result set.
  const filteredScenarios = scenarios;

  const filteredScenarioIds = filteredScenarios.map((s) => s.id);
  const allSelected =
    filteredScenarioIds.length > 0 &&
    filteredScenarioIds.every((id) => selectedScenarios.includes(id));

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedScenarios((prev) =>
        prev.filter((id) => !filteredScenarioIds.includes(id)),
      );
    } else {
      setSelectedScenarios((prev) =>
        Array.from(new Set([...prev, ...filteredScenarioIds])),
      );
    }
  };

  // Derive which channels the agent has configured. Prefer the new channel
  // jsonb fields; fall back to legacy columns so agents not yet migrated
  // still work.
  const agentConfiguredChannels = (() => {
    if (!liveAgent) return { phone: false, web: false, text: false };
    const legacyWebTypes = ["livekit_managed", "daily_managed", "websocket", "pipecat_livekit", "external_vapi", "external_retell"];
    return {
      phone:
        !!liveAgent?.phone_channel?.provider ||
        !!liveAgent?.phone_number ||
        !!liveAgent?.twilio_phone_number ||
        ["pipecat_twilio", "vapi"].includes(liveAgent?.integration_type),
      web:
        !!liveAgent?.web_channel?.provider ||
        !!liveAgent?.web_call_url ||
        legacyWebTypes.includes(liveAgent?.integration_type),
      text:
        !!liveAgent?.text_channel?.provider ||
        !!liveAgent?.text_config?.text_endpoint ||
        !!liveAgent?.text_config?.text_websocket_url,
    };
  })();

  const handleRunSelectedScenarios = () => {
    if (selectedScenarios.length === 0) {
      alert("Please select at least one scenario to run");
      return;
    }
    const { phone, web, text } = agentConfiguredChannels;
    const count = [phone, web, text].filter(Boolean).length;
    if (count === 0) {
      setError(
        "This agent has no channels configured. Add Phone, Web, or Text in Agent settings before running.",
      );
      return;
    }
    if (count > 1) {
      setShowCallMethodModal(true);
      return;
    }
    // Exactly one channel configured — proceed directly
    const only = phone ? "phone" : web ? "web" : "text";
    submitBatchRun(only);
  };

  const submitBatchRun = async (channelArg, scenarioIds = selectedScenarios) => {
    setShowCallMethodModal(false);
    // channelArg may be "phone" | "web" | "text" OR legacy "web_call" / "phone"
    const channel =
      channelArg === "web_call" ? "web" :
      channelArg === "phone" ? "phone" :
      channelArg === "web"   ? "web" :
      channelArg === "text"  ? "text" : "phone";
    const voiceCallMode = channel === "phone" ? "phone" : "web_call";
    try {
      setLoading(true);
      setError(null);

      const scenariosToRun = filteredScenarios.filter((scenario) =>
        scenarioIds.includes(scenario.id),
      );
      const scenariosData = scenariosToRun.map((scenario) => ({
        id: scenario.id,
        metrics: scenario.metrics?.map((m) => m.id) || [],
      }));

      const batchName = `Batch Run - ${new Date().toLocaleString()}`;
      const batchData = {
        batch: {
          name: batchName,
          batch_type: "scenario",
          client_id: currentWorkspace.id,
          agent_id: liveAgent?.id,
          voice_call_mode: voiceCallMode,
          channel,
          testing_mode: channel === "text" ? "text" : "voice",
        },
        scenarios: JSON.stringify(scenariosData),
      };

      const response = await scenariosAPI.createBatch(
        batchData,
        currentWorkspace.id,
      );
      if (response.data) {
        setSuccessBatch(response.data);
        setSelectedScenarios([]);
      }
    } catch (err) {
      console.error("Error creating batch:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to create batch run",
      );
      alert(
        `Failed to create batch run: ${err.response?.data?.error || err.message || "Unknown error"}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRunSingleScenario = (scenarioId) => {
    const { phone, web, text } = agentConfiguredChannels;
    const count = [phone, web, text].filter(Boolean).length;
    if (count === 0) {
      setError(
        "This agent has no channels configured. Add Phone, Web, or Text in Agent settings before running.",
      );
      return;
    }
    if (count > 1) {
      // Multiple channels — let the user pick; the modal runs the current selection.
      setSelectedScenarios([scenarioId]);
      setShowCallMethodModal(true);
      return;
    }
    const only = phone ? "phone" : web ? "web" : "text";
    submitBatchRun(only, [scenarioId]);
  };

  const handleArchiveSelected = async () => {
    if (selectedScenarios.length === 0) return;
    if (
      !window.confirm(
        `Archive ${selectedScenarios.length} selected scenario${selectedScenarios.length > 1 ? "s" : ""}? Runs will be preserved.`,
      )
    ) {
      return;
    }
    try {
      await Promise.all(
        selectedScenarios.map((id) => scenariosAPI.archiveScenario(id, currentWorkspace.id)),
      );
      setSelectedScenarios([]);
      fetchScenarios();
    } catch (err) {
      setError("Failed to archive selected scenarios");
      console.error("Error archiving selected scenarios:", err);
    }
  };

  const handleScheduleSelected = () => {
    if (selectedScenarios.length === 0) {
      alert("Please select at least one scenario to schedule");
      return;
    }
    setScheduleScenarioIds([...selectedScenarios]);
    goToTab("schedules");
  };

  // Persona handlers
  const handleTalkToPersona = (persona) => {
    setSelectedPersona(persona);
    setIsCallModalOpen(true);
  };

  const handleCloseCallModal = () => {
    setIsCallModalOpen(false);
    setSelectedPersona(null);
  };

  const personaTabs = [
    {
      id: "all",
      name: "All Personas",
      count: personas.length,
    },
    {
      id: "predefined",
      name: "Pre-defined",
      count: personas.filter((p) => !p.is_custom).length,
    },
    {
      id: "custom",
      name: "Custom",
      count: personas.filter((p) => p.is_custom).length,
    },
  ];

  const filteredPersonasList = personas.filter((persona) =>
    personaSubTab === "all" ? true :
    personaSubTab === "predefined" ? !persona.is_custom : persona.is_custom,
  );

  return (
    <>
      <div className="px-4">
        {/* Tabs — pill segmented control (matches /evaluation/metrics) */}
        <div className="flex space-x-1 mb-6 bg-muted rounded-lg p-1 w-fit">
          <button
            type="button"
            onClick={() => goToTab("scenario")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "scenario"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Scenario
          </button>
          <button
            type="button"
            onClick={() => goToTab("persona")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "persona"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Persona Library
          </button>
          <button
            type="button"
            onClick={() => goToTab("attributes")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "attributes"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Conversation Attributes
          </button>
          <button
            type="button"
            onClick={() => {
              goToTab("schedules");
              setScheduleScenarioIds([]);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "schedules"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Automated Schedules
          </button>
        </div>

        {activeTab === "scenario" && (
          <div>
            {/* Main Content */}
            <div className="w-full">
              <PageHeader description="Manage and simulate your test scenarios.">
                <SearchInput
                  className="w-48"
                  placeholder="Search scenarios..."
                  value={search}
                  onChange={(e) => changeSearch(e.target.value)}
                />
                {agents.length > 0 && (
                  <AgentPicker
                    agents={agents}
                    selectedAgent={selectedAgent}
                    setSelectedAgent={setSelectedAgent}
                    onAdd={() => navigate("/ai-testing-agent/create")}
                  />
                )}
                {canWriteScenario && canAiGenerate && (
                  <Button
                    variant="primary"
                    onClick={() => navigate("/live-simulations/generate")}
                  >
                    <Sparkles className="h-5 w-5" />
                    <span>Generate by AI</span>
                  </Button>
                )}
                {canWriteScenario && (
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/live-simulations/scenarios/create")}
                  >
                    <Plus className="h-4 w-4 stroke-2" />
                    <span>Create Scenario</span>
                  </Button>
                )}
                <div className="relative" ref={columnsMenuRef}>
                  <button
                    onClick={() => setShowColumnsMenu((v) => !v)}
                    className="p-3 border border-input rounded-lg bg-background hover:bg-muted transition-colors"
                    title="Columns"
                  >
                    <Columns2 className="h-4 w-4 text-muted-foreground" />
                  </button>
                  {showColumnsMenu && (
                    <div className="absolute right-0 mt-1 w-48 bg-card border border-border rounded-lg shadow-lg z-20 p-1.5">
                      {SCENARIO_COLUMNS.map((col) => (
                        <label
                          key={col.key}
                          className="flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-muted cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={visibleColumns.has(col.key)}
                            onChange={() => toggleColumn(col.key)}
                            className="h-4 w-4 rounded border-input text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-foreground">
                            {col.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </PageHeader>

              {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              {!loading && filteredScenarios.length > 0 && (
                <div className="flex items-center justify-start mb-4 gap-3">
                  <ScenarioSelectionToolbar
                    selectedIds={selectedScenarios}
                    canWrite={canWriteScenario}
                    canStressTest={canStressTest}
                    onRunSelected={handleRunSelectedScenarios}
                    onScheduleSelected={handleScheduleSelected}
                    onArchiveSelected={handleArchiveSelected}
                    onRunStressTest={(scenarioId) =>
                      navigate(`/live-simulations/scenarios/${scenarioId}/stress-test`)
                    }
                    loading={loading}
                  />
                </div>
              )}

              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
                </div>
              ) : filteredScenarios.length === 0 ? (
                <EmptyState
                  compact
                  icon={Sparkles}
                  title={
                    debouncedSearch
                      ? `No scenarios match "${debouncedSearch}"`
                      : selectedAgent
                        ? `No scenarios for ${selectedAgent.name}`
                        : "No Scenarios Yet"
                  }
                  description={
                    debouncedSearch
                      ? "Try a different search term."
                      : selectedAgent
                        ? "Try selecting a different agent or create a new scenario."
                        : "Create your first scenario or generate one using AI to get started."
                  }
                  primaryAction={
                    canWriteScenario && canAiGenerate
                      ? {
                          label: "Generate with AI",
                          icon: Sparkles,
                          onClick: () => navigate("/live-simulations/generate"),
                        }
                      : undefined
                  }
                  secondaryAction={
                    canWriteScenario
                      ? {
                          label: "Create Manually",
                          icon: Plus,
                          onClick: () =>
                            navigate("/live-simulations/scenarios/create"),
                        }
                      : undefined
                  }
                />
              ) : (
                <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full table-fixed divide-y divide-border">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="w-10 px-4 py-3">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-input text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                              checked={allSelected}
                              onChange={handleSelectAll}
                            />
                          </th>
                          {visibleColumns.has("name") && (
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                              Name
                            </th>
                          )}
                          {visibleColumns.has("created_at") && (
                            <th className="w-36 px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                              Created
                            </th>
                          )}
                          {visibleColumns.has("id") && (
                            <th className="w-24 px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                              ID
                            </th>
                          )}
                          {visibleColumns.has("type") && (
                            <th className="w-28 px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                              Type
                            </th>
                          )}
                          {visibleColumns.has("language") && (
                            <th className="w-32 px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                              Language
                            </th>
                          )}
                          {visibleColumns.has("persona") && (
                            <th className="w-44 px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                              Persona
                            </th>
                          )}
                          {visibleColumns.has("metrics") && (
                            <th className="w-44 px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                              Metrics
                            </th>
                          )}
                          {visibleColumns.has("tags") && (
                            <th className="w-44 px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                              Tags
                            </th>
                          )}
                          <th className="w-28 px-2 py-3"></th>
                        </tr>
                      </thead>
                      <tbody className="bg-card divide-y divide-border">
                        {filteredScenarios.map((scenario) => (
                          <tr
                            key={scenario.id}
                            className={`hover:bg-muted/50 transition-colors group cursor-pointer ${selectedScenarios.includes(scenario.id) ? "bg-indigo-50" : ""}`}
                            onClick={() =>
                              navigate(
                                `/live-simulations/scenarios/${scenario.id}/edit`,
                              )
                            }
                          >
                            <td
                              className="w-10 px-4 py-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-input text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                checked={selectedScenarios.includes(
                                  scenario.id,
                                )}
                                onChange={() =>
                                  handleCheckboxChange(scenario.id)
                                }
                              />
                            </td>
                            {visibleColumns.has("name") && (
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-sm font-medium text-foreground truncate">
                                    {scenario.name}
                                  </span>
                                  {scenario.is_global ? (
                                    <Badge
                                      variant="info"
                                      className="group/share shrink-0 px-1.5"
                                      title="Shared with every agent in this workspace"
                                    >
                                      <Globe className="size-3" />
                                      <span className="hidden whitespace-nowrap group-hover/share:inline">
                                        Shared with every agent
                                      </span>
                                    </Badge>
                                  ) : scenario.agents?.length > 1 ? (
                                    <Badge
                                      variant="info"
                                      className="group/share shrink-0 px-1.5"
                                      title={`Shared with ${scenario.agents.length} test agents — editing applies to all`}
                                    >
                                      <Users className="size-3" />
                                      <span className="group-hover/share:hidden">
                                        {scenario.agents.length}
                                      </span>
                                      <span className="hidden whitespace-nowrap group-hover/share:inline">
                                        Shared with {scenario.agents.length} test
                                        agents
                                      </span>
                                    </Badge>
                                  ) : null}
                                </div>
                              </td>
                            )}
                            {visibleColumns.has("created_at") && (
                              <td className="w-36 px-4 py-3">
                                <span className="text-sm text-muted-foreground whitespace-nowrap">
                                  {scenario.created_at
                                    ? new Date(
                                        scenario.created_at,
                                      ).toLocaleDateString(undefined, {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                      })
                                    : "—"}
                                </span>
                              </td>
                            )}
                            {visibleColumns.has("id") && (
                              <td className="w-24 px-4 py-3">
                                <span className="text-xs font-mono text-muted-foreground truncate block">
                                  {scenario.id}
                                </span>
                              </td>
                            )}
                            {visibleColumns.has("type") && (
                              <td className="w-28 px-4 py-3">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    scenario.scenario_mode === "conditional"
                                      ? "bg-purple-50 text-purple-700"
                                      : "bg-blue-50 text-blue-700"
                                  }`}
                                >
                                  {scenario.scenario_mode === "conditional"
                                    ? "Condition"
                                    : "Instructions"}
                                </span>
                              </td>
                            )}
                            {visibleColumns.has("language") && (
                              <td className="w-32 px-4 py-3">
                                <span className="text-sm text-muted-foreground truncate block">
                                  {scenario.additional_info?.language ||
                                    "English"}
                                </span>
                              </td>
                            )}
                            {visibleColumns.has("persona") && (
                              <td className="w-44 px-4 py-3">
                                <span className="text-sm text-muted-foreground truncate block">
                                  {scenario?.persona || "—"}
                                </span>
                              </td>
                            )}
                            {visibleColumns.has("metrics") && (
                              <td className="w-44 px-4 py-3">
                                <div className="flex items-center gap-1">
                                  {scenario.metrics &&
                                  scenario.metrics.length > 0 ? (
                                    <>
                                      {scenario.metrics
                                        .slice(0, 1)
                                        .map((metric) => (
                                          <Badge
                                            key={metric.id}
                                            variant="muted"
                                            className="max-w-[140px] justify-start overflow-hidden"
                                            title={metric.name}
                                          >
                                            <span className="truncate min-w-0">
                                              {metric.name}
                                            </span>
                                          </Badge>
                                        ))}
                                      {scenario.metrics.length > 1 && (
                                        <Badge variant="muted">
                                          +{scenario.metrics.length - 1}
                                        </Badge>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-sm text-muted-foreground">
                                      —
                                    </span>
                                  )}
                                </div>
                              </td>
                            )}
                            {visibleColumns.has("tags") && (
                              <td className="w-44 px-4 py-3">
                                <div className="flex flex-wrap gap-1">
                                  {scenario.tags && scenario.tags.length > 0 ? (
                                    scenario.tags.map((tag, idx) => (
                                      <Badge key={idx} variant="muted">
                                        {tag}
                                      </Badge>
                                    ))
                                  ) : (
                                    <span className="text-sm text-muted-foreground">
                                      —
                                    </span>
                                  )}
                                </div>
                              </td>
                            )}
                            <td
                              className="w-28 px-2 py-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {canWriteScenario && (
                                <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRunSingleScenario(scenario.id);
                                    }}
                                    className="p-1.5 rounded hover:bg-primary/10 text-primary transition-colors"
                                    title="Run Scenario"
                                  >
                                    <Play className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(
                                        `/live-simulations/scenarios/${scenario.id}/edit`,
                                      );
                                    }}
                                    className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                    title="Edit Scenario"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleArchive(scenario.id);
                                    }}
                                    className="p-1.5 rounded hover:bg-amber-50 text-muted-foreground hover:text-amber-600 transition-colors"
                                    title="Archive Scenario (runs preserved)"
                                  >
                                    <Archive className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        )}

        {activeTab === "persona" && (
          <div>
            <PageHeader description="Browse, manage, and create voice personas for your agents.">
              <select
                value={personaSubTab}
                onChange={(e) => setPersonaSubTab(e.target.value)}
                className="pl-3 pr-8 py-2 text-sm font-medium text-foreground bg-background border border-input rounded-lg shadow-sm hover:bg-muted focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring cursor-pointer"
              >
                {personaTabs.map((tab) => (
                  <option key={tab.id} value={tab.id}>
                    {tab.name} ({tab.count})
                  </option>
                ))}
              </select>
              {canWritePersona && (
                <Button
                  variant="secondary"
                  onClick={() => navigate("/voice-persona/create")}
                >
                  <Plus className="h-4 w-4 stroke-2" />
                  <span>Create Persona</span>
                </Button>
              )}
            </PageHeader>

            {personaError && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-700">{personaError}</p>
              </div>
            )}

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
              </div>
            ) : filteredPersonasList.length > 0 ? (
              <div className="border border-border rounded-lg divide-y divide-border">
                {filteredPersonasList.map((persona) => {
                  const isEnabled =
                    personaEnabledStates[persona.id] !== undefined
                      ? personaEnabledStates[persona.id]
                      : true;

                  const toggleEnabled = () => {
                    setPersonaEnabledStates((prev) => ({
                      ...prev,
                      [persona.id]: !isEnabled,
                    }));
                  };

                  return (
                    <div
                      key={persona.id}
                      className="flex items-center justify-between px-5 py-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <button
                          onClick={() => handleTalkToPersona(persona)}
                          className="flex-shrink-0 p-2 rounded-lg border border-border hover:border-muted-foreground hover:bg-muted transition-colors"
                          title="Talk to persona"
                        >
                          <Mic className="h-4 w-4 text-muted-foreground" />
                        </button>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground truncate">
                              {persona.name}
                            </span>
                            {persona.is_custom && (
                              <Badge variant="muted" className="rounded-full">
                                Custom
                              </Badge>
                            )}
                          </div>
                          {persona.description && (
                            <p className="text-sm text-muted-foreground mt-0.5 truncate max-w-xl">
                              {persona.description}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 shrink-0 ml-4">
                        {canWritePersona && (
                          <button
                            onClick={toggleEnabled}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                              isEnabled ? "bg-black" : "bg-gray-200"
                            }`}
                          >
                            <span
                              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-card transition-transform ${
                                isEnabled ? "translate-x-4" : "translate-x-0.5"
                              }`}
                            />
                          </button>
                        )}

                        {canWritePersona && (
                          <button
                            onClick={() =>
                              navigate(`/voice-persona/edit/${persona.id}`)
                            }
                            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}

                        {canWritePersona && (
                          <DropdownMenu
                            items={[
                              {
                                label: "Edit",
                                onClick: () =>
                                  navigate(`/voice-persona/edit/${persona.id}`),
                              },
                              { divider: true },
                              {
                                label: "Delete",
                                onClick: () => console.log("Delete"),
                                variant: "danger",
                              },
                            ]}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                compact
                icon={Mic}
                title={
                  personaSubTab === "predefined"
                    ? "No Pre-defined Personas"
                    : "No Custom Personas Yet"
                }
                description={
                  personaSubTab === "predefined"
                    ? "No pre-defined personas are available at the moment."
                    : "Create your first custom voice persona to personalize your voice interactions."
                }
                primaryAction={
                  personaSubTab === "custom" && canWritePersona
                    ? {
                        label: "Create Persona",
                        onClick: () => navigate("/voice-persona/create"),
                      }
                    : undefined
                }
              />
            )}
          </div>
        )}

        {activeTab === "attributes" && <ConversationAttributesTab />}

        {activeTab === "schedules" && (
          <ScheduledRuns
            preSelectedScenarioIds={scheduleScenarioIds}
            onScheduleCreated={() => setScheduleScenarioIds([])}
          />
        )}
      </div>

      <CallModal
        persona={selectedPersona}
        isOpen={isCallModalOpen}
        onClose={handleCloseCallModal}
      />

      {/* Channel picker modal */}
      {showCallMethodModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-4">
            <h2 className="text-lg font-semibold text-foreground mb-1">
              Choose Channel
            </h2>
            <p className="text-sm text-muted-foreground mb-5">
              This agent supports multiple channels. Which one would you like to test?
            </p>
            <div className="flex flex-col gap-3">
              {agentConfiguredChannels.phone && (
                <button
                  onClick={() => submitBatchRun("phone")}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border-2 border-border hover:border-muted-foreground hover:bg-muted transition-colors text-left"
                >
                  <div className="bg-muted rounded-lg p-2">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Phone</p>
                    {liveAgent?.phone_number && (
                      <p className="text-xs text-muted-foreground">{liveAgent?.phone_number}</p>
                    )}
                  </div>
                </button>
              )}
              {agentConfiguredChannels.web && (
                <button
                  onClick={() => submitBatchRun("web")}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border-2 border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 transition-colors text-left"
                >
                  <div className="bg-indigo-100 rounded-lg p-2">
                    <Globe className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Web</p>
                    {liveAgent?.web_call_url && (
                      <p className="text-xs text-muted-foreground">{liveAgent?.web_call_url}</p>
                    )}
                  </div>
                </button>
              )}
              {agentConfiguredChannels.text && (
                <button
                  onClick={() => submitBatchRun("text")}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border-2 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 transition-colors text-left"
                >
                  <div className="bg-emerald-100 rounded-lg p-2">
                    <MessageSquare className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Text</p>
                    <p className="text-xs text-muted-foreground">Text-based testing</p>
                  </div>
                </button>
              )}
            </div>
            <button
              onClick={() => setShowCallMethodModal(false)}
              className="mt-4 w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Batch queued success modal */}
      {successBatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md mx-4 p-4 text-center">
            <div className="flex items-center justify-center mb-5">
              <div className="bg-green-100 rounded-full p-4">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-2">
              Scenarios queued successfully!
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Your batch run{" "}
              <span className="font-medium text-foreground">
                "{successBatch.name}"
              </span>{" "}
              has been created and the calls are being initiated.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  navigate(`/batch-run/${successBatch.id}`);
                  setSuccessBatch(null);
                }}
                className="flex items-center justify-center gap-2 w-full px-5 py-2.5 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors"
              >
                View Batch Run
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setSuccessBatch(null)}
                className="w-full px-5 py-2.5 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
              >
                Stay on this page
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
